import { useEffect,useId,useRef,useState } from 'react';

declare global {
  interface Window {
    tinymce?: {
      init:(settings:Record<string,unknown>)=>Promise<Array<{remove:()=>void;getContent:()=>string;setContent:(value:string)=>void;on:(event:string,handler:()=>void)=>void}>>;
      get:(id:string)=>{remove:()=>void;setContent:(value:string)=>void}|null;
    };
    __pintopTinyMceLoader?: Promise<void>;
  }
}

const tinymceUrl='https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js';
function loadTinyMce(){
  if(window.tinymce)return Promise.resolve();
  if(window.__pintopTinyMceLoader)return window.__pintopTinyMceLoader;
  window.__pintopTinyMceLoader=new Promise<void>((resolve,reject)=>{
    const existing=document.querySelector<HTMLScriptElement>('script[data-pintop-tinymce="true"]');
    if(existing){existing.addEventListener('load',()=>resolve());existing.addEventListener('error',()=>reject(new Error('TinyMCE could not load.')));return;}
    const script=document.createElement('script');
    script.src=tinymceUrl;
    script.referrerPolicy='origin';
    script.dataset.pintopTinymce='true';
    script.onload=()=>resolve();
    script.onerror=()=>reject(new Error('TinyMCE could not load.'));
    document.head.appendChild(script);
  });
  return window.__pintopTinyMceLoader;
}

export function RichTextEditor({value,onChange,placeholder='',height=460}:{value:string;onChange:(value:string)=>void;placeholder?:string;height?:number}){
  const generatedId=useId().replace(/[^a-zA-Z0-9_-]/g,'');
  const id=`pintop-editor-${generatedId}`;
  const ref=useRef<HTMLTextAreaElement|null>(null);
  const editorRef=useRef<{remove:()=>void;getContent:()=>string;setContent:(value:string)=>void}|null>(null);
  const onChangeRef=useRef(onChange);
  const [fallback,setFallback]=useState(false);
  useEffect(()=>{onChangeRef.current=onChange;},[onChange]);
  useEffect(()=>{let cancelled=false;loadTinyMce().then(async()=>{
    if(cancelled||!ref.current||!window.tinymce)return;
    const editors=await window.tinymce.init({
      target:ref.current,
      height,
      min_height:height,
      menubar:false,
      branding:false,
      promotion:false,
      convert_urls:false,
      skin:'oxide',
      content_css:false,
      content_style:"body{font-family:'Plus Jakarta Sans','Segoe UI',system-ui,Arial,sans-serif;font-size:15.5px;line-height:1.7;color:#1c3644;padding:22px 26px;background:#fff;} h1,h2,h3{font-family:'Fraunces',Georgia,serif;color:#215273;line-height:1.15;margin:0 0 14px;font-weight:600;} p{margin:0 0 14px;} ul,ol{padding-left:1.35rem;} a{color:#2b8790;} table{border-collapse:collapse;width:100%;} td,th{border:1px solid #dbe9e7;padding:8px;}",
      plugins:'autolink lists link table code wordcount',
      toolbar:'undo redo | blocks | bold italic underline forecolor | bullist numlist | alignleft aligncenter alignright | link table | removeformat code',
      placeholder,
      setup:(editor:{on:(event:string,handler:()=>void)=>void;getContent:()=>string})=>{
        const sync=()=>onChangeRef.current(editor.getContent());
        editor.on('change',sync);editor.on('keyup',sync);editor.on('undo',sync);editor.on('redo',sync);
      },
    });
    if(cancelled){editors.forEach(editor=>editor.remove());return;}
    editorRef.current=editors[0]||null;
  }).catch(()=>setFallback(true));return()=>{cancelled=true;editorRef.current?.remove();editorRef.current=null;};},[]);
  useEffect(()=>{const editor=editorRef.current;if(editor&&editor.getContent()!==value)editor.setContent(value||'');},[value]);
  if(fallback)return <textarea rows={Math.max(5,Math.round(height/42))} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}/>;
  return <div className="rich-editor"><textarea id={id} ref={ref} defaultValue={value}/></div>;
}
